// The email-type taxonomy: the exact set of types the classifier is
// instructed to return (`packages/prompts/prompts/type.md`), the vocabulary
// behind the `$type:` JMAP keywords, and the value set `search_emails`
// exposes as a filter.
//
// It lives in @langmail/agents rather than @langmail/database because both
// consumers need it and only one of them may talk to Postgres: the MCP
// service deliberately speaks to Stalwart and Better Auth only, so pulling a
// Postgres-coupled package in to read a string enum would blur that boundary
// (ADR 0010).

import { z } from 'zod'

/**
 * One entry in the taxonomy: the display name Langmail's own surfaces render,
 * and the one-sentence gloss agent-facing tool schemas show when offering the
 * type as a search facet. The gloss is written for a model choosing a filter,
 * so it names what belongs in the type rather than describing it abstractly.
 */
export interface EmailType {
  name: string
  search: string
}

/**
 * The ten classified types, in the order `type.md` lists them. Keys are used
 * verbatim as `$type:` keyword suffixes and as `search_emails` enum values, so
 * they must stay lowercase, slug-stable, and free of RFC 8621's forbidden
 * keyword characters.
 */
export const EMAIL_TYPES = {
  direct: {
    name: '💬 Direct',
    search: 'Person-to-person mail written by an individual, with no platform in between.',
  },
  newsletter: {
    name: '📧 Newsletter',
    search: 'Recurring updates, news digests, and curated broadcast content.',
  },
  promotional: {
    name: '🎯 Promotional',
    search: 'Marketing mail, sales pitches, and discount offers.',
  },
  notification: {
    name: '🔔 Notification',
    search: 'Platform activity alerts and account notices such as billing or subscription problems.',
  },
  ticket: {
    name: '🎫 Ticket',
    search: 'Event tickets: theater, cinema, concerts, conferences.',
  },
  receipt: {
    name: '🧾 Receipt',
    search: 'Transaction receipts and payment confirmations.',
  },
  order: {
    name: '📦 Order',
    search: 'Order confirmations, shipping updates, delivery tracking, returns and refunds.',
  },
  scheduling: {
    name: '📅 Scheduling',
    search: 'Calendar invites and booking confirmations — hotels, flights, restaurants, meetings, appointments.',
  },
  security_alert: {
    name: '🔐 Security Alert',
    search: 'Login alerts, password changes, verification codes, and account recovery notices.',
  },
  terms: {
    name: '📜 Terms',
    search: 'Terms of service and privacy policy updates, and other legal or compliance notices.',
  },
} as const satisfies Record<string, EmailType>

export type EmailTypeUnion = keyof typeof EMAIL_TYPES

/**
 * The taxonomy keys as a non-empty tuple — what `z.enum` and the MCP search
 * enum both need, and the iteration order for "clear every other type keyword"
 * writes.
 */
export const EMAIL_TYPE_VALUES = Object.keys(EMAIL_TYPES) as [EmailTypeUnion, ...EmailTypeUnion[]]

export const EmailTypeSchema = z.enum(EMAIL_TYPE_VALUES)

/**
 * Recorded as the email type when classification fails. Deliberately outside
 * `EMAIL_TYPES`: it is the absence of a classification rather than a type, so
 * it gets no `$type:` keyword, is skipped by the backfill, and is absent from
 * the search enum.
 */
export const UNCLASSIFIED_EMAIL_TYPE = 'other'

/** Narrows a persisted `email_type` string to the taxonomy (excludes `other`). */
export const isEmailType = (value: string): value is EmailTypeUnion => Object.hasOwn(EMAIL_TYPES, value)
